import {
  RfpAnnualOpenEnrollmentEnum,
  extendEnumWithNotDisclosedAndNotOffered,
} from "@platform/openapi-types";

import {
  createEnumFormatter,
  createValueFormatter,
} from "../../../components/PlanEditor/utilities/enumFormatters";

import type { CellSpec } from "./types";

// Shared label text across the PropelX-only extended-field dropdowns below.
const NOT_INCLUDED_LABEL = "Not Included";
const NOT_DISCLOSED_LABEL = "Not Disclosed";
const NOT_OFFERED_LABEL = "Not Offered";

export const annualOpenEnrollmentType: CellSpec = {
  singleClickEdit: true,
  cellType: "dropdown",
  dataType: "string",
  cellEditor: "agRichSelectCellEditor",
  cellEditorParams: {
    allowTyping: true,
    filterList: true,
    searchType: "matchAny",
    values: extendEnumWithNotDisclosedAndNotOffered(
      RfpAnnualOpenEnrollmentEnum,
    ),
    formatValue: createEnumFormatter(),
  },
  valueFormatter: createValueFormatter(),
  description: "Annual open enrollment type",
  validation: {
    required: true,
    allowedValues: extendEnumWithNotDisclosedAndNotOffered(
      RfpAnnualOpenEnrollmentEnum,
    ),
  },
};

export const enumDropdown: CellSpec = {
  singleClickEdit: true,
  cellType: "dropdown",
  dataType: "string",
  cellEditor: "agRichSelectCellEditor",
  cellEditorParams: {
    allowTyping: true,
    filterList: true,
    searchType: "matchAny",
    values: [],
    formatValue: createEnumFormatter(),
  },
  valueFormatter: createValueFormatter(),
  description:
    "Generic enum dropdown (values provided via definition override)",
  validation: {
    required: true,
    allowedValues: [],
  },
};

export const benefitsIncluded: CellSpec = {
  singleClickEdit: true,
  cellType: "dropdown",
  dataType: "string",
  cellEditor: "agRichSelectCellEditor",
  cellEditorParams: {
    values: ["yes", "no"],
    allowTyping: true,
    filterList: true,
    searchType: "matchAny",
    formatValue: createEnumFormatter(),
  },
  valueFormatter: createValueFormatter(),
  description: "Whether benefits are included",
  validation: {
    required: false,
    allowedValues: ["yes", "no"],
  },
};

// PropelX-only field (not in v5 spec), so its dropdown values have no
// generated OpenAPI enum to draw from. Values and labels per the MP Data
// Elements and Value Standards v5 PropelX Presentation spreadsheet
// (Eligibility Hours row, confirmed the same across every LOC in CD-2325).
export const eligibilityHoursValues = [
  "noMinimum",
  "hours20",
  "hours25",
  "hours30",
  "hours35",
  "hours37Point5",
  "hours40",
  "notIncluded",
  "notDisclosed",
];

export const eligibilityHoursLabels: Record<string, string> = {
  noMinimum: "No Minimum",
  hours20: "20 hrs/week",
  hours25: "25 hrs/week",
  hours30: "30 hrs/week",
  hours35: "35 hrs/week",
  hours37Point5: "37.5 hrs/week",
  hours40: "40 hrs/week",
  notIncluded: "Not Included",
  notDisclosed: "Not Disclosed",
};

export const eligibilityHoursDropdown: CellSpec = {
  singleClickEdit: true,
  cellType: "dropdown",
  dataType: "string",
  cellEditor: "agRichSelectCellEditor",
  cellEditorParams: {
    allowTyping: true,
    filterList: true,
    searchType: "matchAny",
    values: eligibilityHoursValues,
    formatValue: createEnumFormatter(eligibilityHoursLabels),
  },
  valueFormatter: createValueFormatter(eligibilityHoursLabels),
  description: "Eligibility hours",
  validation: {
    required: false,
    allowedValues: eligibilityHoursValues,
  },
};

// The PropelX-only extended (non-v5) fields below have no generated OpenAPI
// enum, so their values/labels are sourced directly from the MP Data
// Elements and Value Standards v5 PropelX Presentation spreadsheet (CD-2325).
// Fields marked "not in v5 spec" in that spreadsheet were shipped as plain
// text cells (sharedCellSpecs.text) instead of these dropdowns when the
// extended fields were first added (CD-1659/CD-1660/CD-390 and siblings) —
// CD-2325 is that gap being closed, LOC by LOC.

const buildSimpleEnumDropdown = (
  values: string[],
  labels?: Record<string, string>,
  description?: string,
): CellSpec => ({
  singleClickEdit: true,
  cellType: "dropdown",
  dataType: "string",
  cellEditor: "agRichSelectCellEditor",
  cellEditorParams: {
    allowTyping: true,
    filterList: true,
    searchType: "matchAny",
    values,
    formatValue: createEnumFormatter(labels),
  },
  valueFormatter: createValueFormatter(labels),
  description,
  validation: {
    required: false,
    allowedValues: values,
  },
});

// --- Dental PPO -------------------------------------------------------

export const preventiveAdvantageValues = [
  "yes",
  "no",
  "notOffered",
  "notDisclosed",
];

export const preventiveAdvantageDropdown: CellSpec = buildSimpleEnumDropdown(
  preventiveAdvantageValues,
  undefined,
  "Preventive advantage benefit details",
);

// Shared by Oral Exam Frequency and Cleaning Frequency (identical value list
// per the spreadsheet).
export const dentalTwiceYearlyFrequencyValues = [
  "months6",
  "months12",
  "twiceIn12Months",
];

export const dentalTwiceYearlyFrequencyLabels: Record<string, string> = {
  months6: "1 in 6 months",
  months12: "1 in 12 months",
  twiceIn12Months: "2 in 12 months",
};

export const dentalTwiceYearlyFrequencyDropdown: CellSpec =
  buildSimpleEnumDropdown(
    dentalTwiceYearlyFrequencyValues,
    dentalTwiceYearlyFrequencyLabels,
    "Frequency at which the service is covered",
  );

export const fullMouthXRaysFrequencyValues = ["months36", "months60", "years5"];

export const fullMouthXRaysFrequencyLabels: Record<string, string> = {
  months36: "1 per 36 mo.",
  months60: "1 per 60 mo.",
  years5: "1 per 5 years",
};

export const fullMouthXRaysFrequencyDropdown: CellSpec =
  buildSimpleEnumDropdown(
    fullMouthXRaysFrequencyValues,
    fullMouthXRaysFrequencyLabels,
    "Frequency at which full mouth X-rays are covered",
  );

// Shared by Bridges & Dentures and Crowns (identical value list per the
// spreadsheet).
export const dentalMajorServiceValues = [
  "preventative",
  "basicIncludingPosterior",
  "majorIncludingPosterior",
  "basicAnteriorOnly",
  "majorAnteriorOnly",
  "notOffered",
  "notIncluded",
  "notDisclosed",
];

export const dentalMajorServiceLabels: Record<string, string> = {
  preventative: "Preventative",
  basicIncludingPosterior: "Basic - Including Posterior",
  majorIncludingPosterior: "Major - Including Posterior",
  basicAnteriorOnly: "Basic - Anterior Only",
  majorAnteriorOnly: "Major - Anterior Only",
  notOffered: NOT_OFFERED_LABEL,
  notIncluded: NOT_INCLUDED_LABEL,
  notDisclosed: NOT_DISCLOSED_LABEL,
};

export const dentalMajorServiceDropdown: CellSpec = buildSimpleEnumDropdown(
  dentalMajorServiceValues,
  dentalMajorServiceLabels,
  "Coverage category for the service",
);

// Shared by Bridges & Dentures Frequency and Crowns Frequency (identical
// value list per the spreadsheet).
export const dentalMajorServiceFrequencyValues = [
  "months36",
  "months60",
  "years5",
  "years10",
];

export const dentalMajorServiceFrequencyLabels: Record<string, string> = {
  months36: "1 per 36 mo.",
  months60: "1 per 60 mo.",
  years5: "1 per 5 years",
  years10: "1 per 10 years",
};

export const dentalMajorServiceFrequencyDropdown: CellSpec =
  buildSimpleEnumDropdown(
    dentalMajorServiceFrequencyValues,
    dentalMajorServiceFrequencyLabels,
    "Frequency at which the service is covered",
  );

export const tmjValues = [
  "included",
  "notIncluded",
  "notOffered",
  "notDisclosed",
];

export const tmjDropdown: CellSpec = buildSimpleEnumDropdown(
  tmjValues,
  undefined,
  "TMJ coverage details",
);

// Dental PPO's Work In Progress Included / Open or Closed Contract value
// lists (differ from Dental HMO's below per the spreadsheet, so these are
// LOC-specific rather than shared).
export const dentalPpoWorkInProgressIncludedValues = [
  "includedInFull",
  "includedPriorCarrierOnly",
  "includedLimited",
  "includedReviewRequired",
  "includedPriorPlanRequired",
  "notIncluded",
  "notDisclosed",
];

export const dentalPpoWorkInProgressIncludedLabels: Record<string, string> = {
  includedInFull: "Included in Full",
  includedPriorCarrierOnly: "Included – Prior Carrier Only",
  includedLimited: "Included – Limited",
  includedReviewRequired: "Included – Review Required",
  includedPriorPlanRequired: "Included – Prior Plan Required",
  notIncluded: NOT_INCLUDED_LABEL,
  notDisclosed: NOT_DISCLOSED_LABEL,
};

export const dentalPpoWorkInProgressIncludedDropdown: CellSpec =
  buildSimpleEnumDropdown(
    dentalPpoWorkInProgressIncludedValues,
    dentalPpoWorkInProgressIncludedLabels,
    "Whether work in progress is included",
  );

export const dentalPpoOpenOrClosedContractValues = [
  "openContract",
  "closedContract",
  "closedWithExceptions",
  "notIncluded",
  "notDisclosed",
];

export const dentalPpoOpenOrClosedContractLabels: Record<string, string> = {
  openContract: "Open Contract",
  closedContract: "Closed Contract",
  closedWithExceptions: "Closed – With Exceptions",
  notIncluded: NOT_INCLUDED_LABEL,
  notDisclosed: NOT_DISCLOSED_LABEL,
};

export const dentalPpoOpenOrClosedContractDropdown: CellSpec =
  buildSimpleEnumDropdown(
    dentalPpoOpenOrClosedContractValues,
    dentalPpoOpenOrClosedContractLabels,
    "Whether the contract is open or closed",
  );

// --- Dental HMO ---------------------------------------------------------
// Work In Progress Included / Open or Closed Contract value lists differ
// from Dental PPO's above per the spreadsheet (HMO adds Not Offered to
// both, and drops Not Included from Open or Closed Contract).
export const dentalHmoWorkInProgressIncludedValues = [
  ...dentalPpoWorkInProgressIncludedValues,
  "notOffered",
];

export const dentalHmoWorkInProgressIncludedLabels: Record<string, string> = {
  ...dentalPpoWorkInProgressIncludedLabels,
  notOffered: NOT_OFFERED_LABEL,
};

export const dentalHmoWorkInProgressIncludedDropdown: CellSpec =
  buildSimpleEnumDropdown(
    dentalHmoWorkInProgressIncludedValues,
    dentalHmoWorkInProgressIncludedLabels,
    "Whether work in progress is included",
  );

export const dentalHmoOpenOrClosedContractValues = [
  "openContract",
  "closedContract",
  "closedWithExceptions",
  "notDisclosed",
  "notOffered",
];

export const dentalHmoOpenOrClosedContractLabels: Record<string, string> = {
  openContract: "Open Contract",
  closedContract: "Closed Contract",
  closedWithExceptions: "Closed – With Exceptions",
  notDisclosed: NOT_DISCLOSED_LABEL,
  notOffered: NOT_OFFERED_LABEL,
};

export const dentalHmoOpenOrClosedContractDropdown: CellSpec =
  buildSimpleEnumDropdown(
    dentalHmoOpenOrClosedContractValues,
    dentalHmoOpenOrClosedContractLabels,
    "Whether the contract is open or closed",
  );

// --- Basic Life / Voluntary Life (AD&D riders) --------------------------
// Air Bag, Common Carrier, Repatriation, Seat Belt, and (Basic Life only)
// Travel Aid share the same two-value list on both LOCs per the
// spreadsheet.
export const includedNotIncludedValues = ["included", "notIncluded"];

export const includedNotIncludedDropdown: CellSpec = buildSimpleEnumDropdown(
  includedNotIncludedValues,
  undefined,
  "AD&D rider benefit details",
);

export const eapFaceToFaceVisitsLimitTypeValues = [
  "perIssue",
  "perYear",
  "perLifetime",
  "unlimited",
  "notIncluded",
];

export const eapFaceToFaceVisitsLimitTypeLabels: Record<string, string> = {
  perIssue: "Per Issue",
  perYear: "Per Year",
  perLifetime: "Per Lifetime",
  unlimited: "Unlimited",
  notIncluded: NOT_INCLUDED_LABEL,
};

export const eapFaceToFaceVisitsLimitTypeDropdown: CellSpec =
  buildSimpleEnumDropdown(
    eapFaceToFaceVisitsLimitTypeValues,
    eapFaceToFaceVisitsLimitTypeLabels,
    "Type of limit placed on EAP face-to-face visits",
  );

export const suicideExclusionValues = [
  "included",
  "notIncluded",
  "notOffered",
  "notDisclosed",
];

export const suicideExclusionDropdown: CellSpec = buildSimpleEnumDropdown(
  suicideExclusionValues,
  undefined,
  "Whether death by suicide is excluded from the benefit",
);

export const annualAbilityToIncreaseBenefitValues = [
  "included",
  "notIncluded",
  "notDisclosed",
];

export const annualAbilityToIncreaseBenefitDropdown: CellSpec =
  buildSimpleEnumDropdown(
    annualAbilityToIncreaseBenefitValues,
    undefined,
    "Whether the employee can increase their benefit amount annually",
  );

export const spouseAgeBasisValues = [
  "employeeAge",
  "spouseAge",
  "clientChoice",
  "notApplicable",
  "notDisclosed",
];

export const spouseAgeBasisLabels: Record<string, string> = {
  employeeAge: "EE's Age",
  spouseAge: "SP's Age",
  clientChoice: "Client Choice",
  notApplicable: "N/A",
  notDisclosed: NOT_DISCLOSED_LABEL,
};

export const spouseAgeBasisDropdown: CellSpec = buildSimpleEnumDropdown(
  spouseAgeBasisValues,
  spouseAgeBasisLabels,
  "Whether spouse benefit reductions are based on the employee's or the spouse's age",
);

// --- STD ------------------------------------------------------------

export const partialDisabilityValues = [
  "included",
  "notIncluded",
  "notDisclosed",
  "pending",
];

export const partialDisabilityLabels: Record<string, string> = {
  included: "Included",
  notIncluded: NOT_INCLUDED_LABEL,
  notDisclosed: NOT_DISCLOSED_LABEL,
  pending: "Pending",
};

export const partialDisabilityDropdown: CellSpec = buildSimpleEnumDropdown(
  partialDisabilityValues,
  partialDisabilityLabels,
  "Partial disability benefit details",
);

// Minimum Benefit's type field, built as a type/amount pair via
// buildTypeAmountBenefitPair - STD's own minimumBenefit (CD-2552) and LTD's
// minimumAmount share these options; the amount field displays as a dollar
// amount or a percentage depending on this value, or is disabled when Not
// Disclosed.
export const minimumBenefitTypeValues = [
  "dollarAmount",
  "percentageOfSalary",
  "notDisclosed",
];

export const minimumBenefitTypeLabels: Record<string, string> = {
  dollarAmount: "Dollar Amount",
  percentageOfSalary: "Percentage of Salary",
  notDisclosed: NOT_DISCLOSED_LABEL,
};

export const w2PrepValues = [
  "included",
  "notIncluded",
  "notDisclosed",
  "notOffered",
];

export const w2PrepDropdown: CellSpec = buildSimpleEnumDropdown(
  w2PrepValues,
  undefined,
  "W-2 preparation service details",
);

export const claimsIntakeValues = [
  "paperOnly",
  "paperAndTelephonic",
  "telephonicOnly",
];

export const claimsIntakeLabels: Record<string, string> = {
  paperOnly: "Paper Only",
  paperAndTelephonic: "Paper & Telephonic",
  telephonicOnly: "Telephonic Only",
};

export const claimsIntakeDropdown: CellSpec = buildSimpleEnumDropdown(
  claimsIntakeValues,
  claimsIntakeLabels,
  "Claims intake process details",
);

// Shared by Statutory DI Integration and PFL Integration (identical value
// list per the spreadsheet).
export const diIntegrationValues = [
  "included",
  "notIncluded",
  "notDisclosed",
  "notApplicable",
];

export const diIntegrationLabels: Record<string, string> = {
  included: "Included",
  notIncluded: NOT_INCLUDED_LABEL,
  notDisclosed: NOT_DISCLOSED_LABEL,
  notApplicable: "N/A",
};

export const diIntegrationDropdown: CellSpec = buildSimpleEnumDropdown(
  diIntegrationValues,
  diIntegrationLabels,
  "Disability integration benefit details",
);

export const employerContribution: CellSpec = {
  singleClickEdit: true,
  cellType: "dropdown",
  dataType: "string",
  cellEditor: "agRichSelectCellEditor",
  cellEditorParams: {
    allowTyping: true,
    filterList: true,
    searchType: "matchAny",
    values: [],
    formatValue: createEnumFormatter(),
  },
  valueFormatter: createValueFormatter(),
  description: "Defines employer vs employee contribution structure",
  validation: {
    required: false,
    allowedValues: [],
  },
};

// --- LTD extended (non-v5) fields (CD-2067/CD-2419/CD-2553) --------------
// prudentPerson, activitiesOfDailyLiving, and maximumCapacity share this
// exact value list per the spreadsheet.
export const includedNotIncludedNotDisclosedValues = [
  "included",
  "notIncluded",
  "notDisclosed",
];

export const includedNotIncludedNotDisclosedLabels: Record<string, string> = {
  included: "Included",
  notIncluded: NOT_INCLUDED_LABEL,
  notDisclosed: NOT_DISCLOSED_LABEL,
};

export const prudentPersonDropdown: CellSpec = buildSimpleEnumDropdown(
  includedNotIncludedNotDisclosedValues,
  includedNotIncludedNotDisclosedLabels,
  "Prudent person provision details",
);

export const activitiesOfDailyLivingDropdown: CellSpec =
  buildSimpleEnumDropdown(
    includedNotIncludedNotDisclosedValues,
    includedNotIncludedNotDisclosedLabels,
    "Activities of daily living provision details",
  );

export const maximumCapacityDropdown: CellSpec = buildSimpleEnumDropdown(
  includedNotIncludedNotDisclosedValues,
  includedNotIncludedNotDisclosedLabels,
  "Maximum capacity provision details",
);

export const retirementIncomeProtectionValues = [
  "included",
  "notIncluded",
  "notDisclosed",
  "notOffered",
];

export const retirementIncomeProtectionLabels: Record<string, string> = {
  included: "Included",
  notIncluded: NOT_INCLUDED_LABEL,
  notDisclosed: NOT_DISCLOSED_LABEL,
  notOffered: NOT_OFFERED_LABEL,
};

export const retirementIncomeProtectionDropdown: CellSpec =
  buildSimpleEnumDropdown(
    retirementIncomeProtectionValues,
    retirementIncomeProtectionLabels,
    "Retirement income protection provision details",
  );

export const residencyRestrictionValues = [
  "residencyRequired",
  "residencyNotRequired",
];

export const residencyRestrictionLabels: Record<string, string> = {
  residencyRequired: "Residency Required",
  residencyNotRequired: "Residency Not Required",
};

export const residencyRestrictionDropdown: CellSpec = buildSimpleEnumDropdown(
  residencyRestrictionValues,
  residencyRestrictionLabels,
  "Residency restriction provision details",
);

export const eapValues = [
  "included",
  "notIncluded",
  "notDisclosed",
  "notTiedToLineOfCoverage",
];

export const eapLabels: Record<string, string> = {
  included: "Included",
  notIncluded: NOT_INCLUDED_LABEL,
  notDisclosed: NOT_DISCLOSED_LABEL,
  notTiedToLineOfCoverage: "Not Tied to Line of Coverage",
};

export const eapDropdown: CellSpec = buildSimpleEnumDropdown(
  eapValues,
  eapLabels,
  "Employee assistance program details",
);

export const mandatoryRehabilitationValues = [
  "mandatory",
  "voluntary",
  "enhanced",
  "mandatoryWithEnhanced",
  "voluntaryWithEnhanced",
  "notDisclosed",
  "notIncluded",
];

export const mandatoryRehabilitationLabels: Record<string, string> = {
  mandatory: "Mandatory",
  voluntary: "Voluntary",
  enhanced: "Enhanced",
  mandatoryWithEnhanced: "Mandatory w/ Enhanced",
  voluntaryWithEnhanced: "Voluntary w/ Enhanced",
  notDisclosed: NOT_DISCLOSED_LABEL,
  notIncluded: NOT_INCLUDED_LABEL,
};

export const mandatoryRehabilitationDropdown: CellSpec =
  buildSimpleEnumDropdown(
    mandatoryRehabilitationValues,
    mandatoryRehabilitationLabels,
    "Mandatory rehabilitation provision details",
  );

export const selfReportedLimitationsValues = [
  "months24",
  "months24Lifetime",
  "months24LifetimeCombined",
  "months24CombinedLimited",
  "months24PerOccurrence",
  "months12",
  "months12Lifetime",
  "months12LifetimeCombined",
  "months12CombinedLimited",
  "months12PerOccurrence",
  "noLimit",
  "payableSameAsAnyOtherDisability",
  "notDisclosed",
  "notOffered",
];

export const selfReportedLimitationsLabels: Record<string, string> = {
  months24: "24 months",
  months24Lifetime: "24 months - lifetime",
  months24LifetimeCombined: "24 months - lifetime, combined",
  months24CombinedLimited: "24 months - combined, limited",
  months24PerOccurrence: "24 months per occurrence",
  months12: "12 months",
  months12Lifetime: "12 months - lifetime",
  months12LifetimeCombined: "12 months - lifetime, combined",
  months12CombinedLimited: "12 months - combined, limited",
  months12PerOccurrence: "12 months per occurrence",
  noLimit: "No Limit",
  payableSameAsAnyOtherDisability: "Payable same as any other disability",
  notDisclosed: NOT_DISCLOSED_LABEL,
  notOffered: NOT_OFFERED_LABEL,
};

export const selfReportedLimitationsDropdown: CellSpec =
  buildSimpleEnumDropdown(
    selfReportedLimitationsValues,
    selfReportedLimitationsLabels,
    "Self-reported limitations provision details",
  );

// --- Hospital Indemnity extended (non-v5) field (CD-2076/CD-2542) --------
export const pregnancyValues = [
  "included",
  "notIncluded",
  "notOffered",
  "notDisclosed",
];

export const pregnancyLabels: Record<string, string> = {
  included: "Included",
  notIncluded: NOT_INCLUDED_LABEL,
  notOffered: NOT_OFFERED_LABEL,
  notDisclosed: NOT_DISCLOSED_LABEL,
};

export const pregnancyDropdown: CellSpec = buildSimpleEnumDropdown(
  pregnancyValues,
  pregnancyLabels,
  "Pregnancy coverage details",
);
